import { SCHOOL_YEAR_LABEL, SCHOOL_YEAR_START } from '../data/school-year-2026-2027.ts';
import type { DayIndex } from '../lib/calendar.ts';
import { uid } from '../lib/id.ts';
import { asLevel } from '../lib/levels.ts';
import { RENAMED_SUBJECTS, subjectColor, RITUAL_COLOR } from '../lib/subjects.ts';
import { createDefaults } from './defaults.ts';
import type {
  AppData,
  Evaluation,
  Lesson,
  Meeting,
  Observation,
  Postit,
  Settings,
  Skill,
  SkillStatus,
  SlotIndex,
  Student,
  SyncMeta,
  Task,
  WeekTemplateEntry,
} from './types.ts';

/**
 * Normalise anything that might be sitting in `localStorage` under
 * `jdc_anso_v2`, or arriving from an imported JSON backup, into the current
 * shape.
 *
 * The input is untrusted: it may come from any version of the app going back
 * to the first, from a hand-edited file, or from a partially written save. So
 * every field is read defensively and every collection defaults to empty
 * rather than throwing — losing one malformed record beats refusing to open
 * the journal at all.
 *
 * Carries over the two historical migrations from the original app
 * (`migrateV7` and `normalizeLessonColors`, legacy/index.html:701-719):
 * subject renames, a default `weekStart`, and colours recomputed from subject.
 *
 * Handwriting data-URLs are extracted rather than kept inline: they belong in
 * IndexedDB, and the caller is responsible for writing them there.
 */
export interface NormalizedImport {
  data: AppData;
  /** Stylus notes keyed by observation id, to be written to IndexedDB. */
  handwriting: Record<string, string>;
}

// ---------------------------------------------------------------- primitives

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * A list of strings, from anything.
 *
 * Postgres hands `text[]` back as an array, but a legacy save or a hand-edited
 * backup can hold a scalar or nothing at all.
 */
function strArray(value: unknown): string[] {
  if (typeof value === 'string') return value === '' ? [] : [value];
  return asArray(value)
    .map((item) => str(item).trim())
    .filter((item) => item !== '');
}

function int(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function id(value: unknown): string {
  const s = str(value).trim();
  return s === '' ? uid() : s;
}

/**
 * Preserve an existing sync envelope, or mint one.
 *
 * Rows arriving from a legacy save have never been pushed, so they start
 * dirty; that is what makes the first sign-in upload the whole school year.
 */
function meta(raw: Record<string, unknown>, now: string): SyncMeta {
  return {
    updatedAt: str(raw['updatedAt'], now),
    deletedAt: typeof raw['deletedAt'] === 'string' ? raw['deletedAt'] : null,
    dirty: bool(raw['dirty'], true),
  };
}

// ------------------------------------------------------------------- helpers

/** Apply the historical subject renames. */
function migrateSubject(subject: string): string {
  return RENAMED_SUBJECTS[subject] ?? subject;
}

function asDay(value: unknown): DayIndex {
  const n = int(value, 0);
  return (n >= 0 && n <= 4 ? n : 0) as DayIndex;
}

function asSlot(value: unknown): SlotIndex {
  const n = int(value, 0);
  if (n === -1) return -1;
  return (n >= 0 && n <= 6 ? n : 0) as SlotIndex;
}

const SKILL_STATUSES: readonly SkillStatus[] = ['À travailler', 'En cours', 'Acquis'];

function asSkillStatus(value: unknown): SkillStatus {
  const s = str(value);
  return SKILL_STATUSES.includes(s as SkillStatus) ? (s as SkillStatus) : 'À travailler';
}

/** Lesson colour is always derived from the subject, never trusted from disk. */
function lessonColor(subject: string, slot: SlotIndex): string {
  return slot === -1 ? RITUAL_COLOR : subjectColor(subject);
}

// --------------------------------------------------------------- collections

function normalizeSettings(raw: unknown, now: string): Settings {
  const r = asRecord(raw);
  const fallback = createDefaults().settings;
  return {
    teacher: str(r['teacher'], fallback.teacher),
    // Used to be free text; it now keys the référentiel, so coerce it.
    className: asLevel(r['className']),
    schoolYear: str(r['schoolYear'], SCHOOL_YEAR_LABEL),
    ...meta(r, now),
  };
}

function normalizeStudent(raw: unknown, now: string): Student {
  const r = asRecord(raw);
  const first = str(r['firstName']);
  const last = str(r['lastName']);
  // Older records only ever had `name`; newer ones split it in two.
  const name = str(r['name']) || [first, last].filter(Boolean).join(' ');

  return {
    id: id(r['id']),
    name,
    firstName: first,
    lastName: last,
    birth: str(r['birth']),
    philo: str(r['philo']),
    ...meta(r, now),
  };
}

function normalizeTask(raw: unknown, now: string): Task {
  const r = asRecord(raw);
  return {
    id: id(r['id']),
    text: str(r['text']),
    done: bool(r['done']),
    due: str(r['due']),
    ...meta(r, now),
  };
}

function normalizePostit(raw: unknown, now: string): Postit {
  const r = asRecord(raw);
  return {
    id: id(r['id']),
    text: str(r['text']),
    color: str(r['color'], '#fff2b9'),
    ...meta(r, now),
  };
}

function normalizeLesson(raw: unknown, now: string): Lesson {
  const r = asRecord(raw);
  const subject = migrateSubject(str(r['subject'], 'Français'));
  const slot = asSlot(r['slot']);

  return {
    id: id(r['id']),
    // Lessons predating the yearly planner had no week; they belong to week 1.
    weekStart: str(r['weekStart']) || SCHOOL_YEAR_START,
    day: asDay(r['day']),
    slot,
    subject,
    domain: str(r['domain']),
    color: lessonColor(subject, slot),
    lessonNumber: str(r['lessonNumber']),
    title: str(r['title']),
    detail: str(r['detail']),
    competenceIds: strArray(r['competenceIds']),
    competence: str(r['competence']),
    material: str(r['material']),
    differentiation: str(r['differentiation']),
    ...meta(r, now),
  };
}

function normalizeTemplateEntry(raw: unknown, now: string): WeekTemplateEntry {
  const r = asRecord(raw);
  const subject = migrateSubject(str(r['subject'], 'Français'));
  const slot = asSlot(r['slot']);

  return {
    // Template rows used to be stored with `id: null`; they need real ids now
    // that they sync like everything else.
    id: id(r['id']),
    day: asDay(r['day']),
    slot,
    subject,
    domain: str(r['domain']),
    color: lessonColor(subject, slot),
    lessonNumber: str(r['lessonNumber']),
    title: str(r['title']),
    detail: str(r['detail']),
    competenceIds: strArray(r['competenceIds']),
    competence: str(r['competence']),
    material: str(r['material']),
    differentiation: str(r['differentiation']),
    ...meta(r, now),
  };
}

function normalizeObservation(
  raw: unknown,
  now: string,
  handwriting: Record<string, string>,
): Observation {
  const r = asRecord(raw);
  const observationId = id(r['id']);

  // Legacy shape kept the PNG inline under `handwriting`; pull it out.
  const inline = str(r['handwriting']);
  if (inline.startsWith('data:image')) handwriting[observationId] = inline;

  const hasHandwriting = inline.startsWith('data:image') || bool(r['hasHandwriting']);

  return {
    id: observationId,
    studentId: str(r['studentId']),
    date: str(r['date']),
    lessonId: str(r['lessonId']),
    extraLessonIds: strArray(r['extraLessonIds']),
    subject: migrateSubject(str(r['subject'])),
    difficulty: str(r['difficulty']),
    note: str(r['note']),
    support: str(r['support']),
    hasHandwriting,
    handwritingPath: typeof r['handwritingPath'] === 'string' ? r['handwritingPath'] : null,
    ...meta(r, now),
  };
}

function normalizeEvaluation(raw: unknown, now: string): Evaluation {
  const r = asRecord(raw);
  return {
    id: id(r['id']),
    studentId: str(r['studentId']),
    subject: migrateSubject(str(r['subject'])),
    domain: str(r['domain']),
    lessonId: str(r['lessonId']),
    extraLessonIds: strArray(r['extraLessonIds']),
    title: str(r['title']),
    score: str(r['score']),
    max: str(r['max'], '20'),
    period: str(r['period'], '1'),
    date: str(r['date']),
    ...meta(r, now),
  };
}

function normalizeMeeting(raw: unknown, now: string): Meeting {
  const r = asRecord(raw);
  return {
    id: id(r['id']),
    date: str(r['date']),
    title: str(r['title']),
    notes: str(r['notes']),
    ...meta(r, now),
  };
}

function normalizeSkill(raw: unknown, now: string): Skill {
  const r = asRecord(raw);
  return {
    id: id(r['id']),
    subject: migrateSubject(str(r['subject'])),
    status: asSkillStatus(r['status']),
    title: str(r['title']),
    studentId: str(r['studentId']),
    ...meta(r, now),
  };
}

// ---------------------------------------------------------------- entry point

export function normalizeAppData(raw: unknown): NormalizedImport {
  const now = new Date().toISOString();
  const r = asRecord(raw);
  const handwriting: Record<string, string> = {};

  // A persisted zustand store nests the payload under `state`; a raw legacy
  // save or a JSON backup does not.
  const source = 'state' in r ? asRecord(r['state']) : r;

  const data: AppData = {
    settings: normalizeSettings(source['settings'], now),
    students: asArray(source['students']).map((x) => normalizeStudent(x, now)),
    tasks: asArray(source['tasks']).map((x) => normalizeTask(x, now)),
    postits: asArray(source['postits']).map((x) => normalizePostit(x, now)),
    lessons: asArray(source['lessons']).map((x) => normalizeLesson(x, now)),
    observations: asArray(source['observations']).map((x) =>
      normalizeObservation(x, now, handwriting),
    ),
    evaluations: asArray(source['evaluations']).map((x) => normalizeEvaluation(x, now)),
    meetings: asArray(source['meetings']).map((x) => normalizeMeeting(x, now)),
    skills: asArray(source['skills']).map((x) => normalizeSkill(x, now)),
    weekTemplate: asArray(source['weekTemplate']).map((x) => normalizeTemplateEntry(x, now)),
  };

  return { data, handwriting };
}

/**
 * True when a value looks like it holds real user data, as opposed to an empty
 * or unreadable save. Used to decide whether to seed a fresh install.
 */
export function hasAnyContent(data: AppData): boolean {
  return (
    data.students.length > 0 ||
    data.lessons.length > 0 ||
    data.observations.length > 0 ||
    data.evaluations.length > 0 ||
    data.meetings.length > 0 ||
    data.skills.length > 0 ||
    data.weekTemplate.length > 0
  );
}
